import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { getPaymentStatus } from "@/lib/api";
import BlackHole from "@/components/BlackHole";

/**
 * PaymentSuccess — verifies the payment then plays a cinematic swallow:
 * each letter of the user's message is animated toward the singularity,
 * followed by SWALLOWED. — You are one of N.
 *
 * We don't have the original message body here (privacy-first: it's on the
 * server, decoupled from the client). We use a symbolic phrase drawn from
 * the visitor's category, which preserves the ritual feeling without ever
 * echoing the private content back.
 */

const SYMBOLIC_PHRASE = "your message is falling";

function SwallowLetters({ phrase, phase }) {
  const letters = useMemo(() => phrase.split(""), [phrase]);

  // Randomised trajectory per letter — resolved once
  const trajectories = useMemo(
    () =>
      letters.map((_, i) => ({
        dx: (Math.random() - 0.5) * 300,
        dy: (Math.random() - 0.5) * 240 - 40,
        rot: (Math.random() - 0.5) * 720,
        delay: 40 + i * 55 + Math.random() * 120,
      })),
    [letters]
  );

  return (
    <div className="font-serif" style={{
        fontSize: "clamp(22px, 4.2vw, 44px)",
        letterSpacing: "-0.005em",
        lineHeight: 1.2,
        color: "rgba(255,240,220,0.92)",
        textShadow: "0 0 24px rgba(255,220,180,0.35)",
        whiteSpace: "nowrap",
        fontVariationSettings: '"opsz" 96, "SOFT" 20',
      }}
      aria-hidden="true"
      data-testid="swallow-letters"
    >
      {letters.map((ch, i) => {
        const t = trajectories[i];
        const swallowing = phase === "deforming" || phase === "swallowed" || phase === "done";
        const style = {
          display: "inline-block",
          transition: `transform 1800ms cubic-bezier(0.19,1,0.22,1) ${t.delay}ms, opacity 1600ms cubic-bezier(0.19,1,0.22,1) ${t.delay}ms, filter 1600ms ease ${t.delay}ms`,
          transform: swallowing
            ? `translate(${t.dx}px, ${t.dy}px) scale(0.05) rotate(${t.rot}deg)`
            : "translate(0,0) scale(1) rotate(0)",
          opacity: swallowing ? 0 : 1,
          filter: swallowing ? "blur(6px)" : "blur(0)",
          willChange: "transform, opacity, filter",
        };
        return (
          <span key={i} style={style}>
            {ch === " " ? "\u00A0" : ch}
          </span>
        );
      })}
    </div>
  );
}

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ payment_status: "pending", swallowed_number: null, total: 0 });
  const [phase, setPhase] = useState("verifying"); // verifying → showing → deforming → swallowed → done
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const pollingRef = useRef(null);

  useEffect(() => {
    if (!sessionId) return;
    let tries = 0;
    const tick = async () => {
      tries += 1;
      try {
        const r = await getPaymentStatus(sessionId);
        setState(r);
        if (r.payment_status === "paid" && r.swallowed_number) {
          clearInterval(pollingRef.current);
          setPhase("showing");
          setTimeout(() => setPhase("deforming"), 1400);
          setTimeout(() => setPhase("swallowed"), 3800);
          setTimeout(() => setPhase("done"), 5400);
          return;
        }
        if (r.payment_status === "failed" || r.payment_status === "expired") {
          clearInterval(pollingRef.current);
          setPhase("failed");
        }
        if (tries > 40) {
          clearInterval(pollingRef.current);
          setPhase("timeout");
        }
      } catch (e) {
        console.error(e);
      }
    };
    tick();
    pollingRef.current = setInterval(tick, 2000);
    return () => clearInterval(pollingRef.current);
  }, [sessionId]);

  useEffect(() => {
    document.body.classList.add("void-lock");
    return () => document.body.classList.remove("void-lock");
  }, []);

  const nf = new Intl.NumberFormat("en-US");
  const total = nf.format(state.total || 0);
  const target = nf.format(1000000);

  const shareText = `I just fed something to THE VOID.\n\n${total} / ${target}\n\nSomething happens at 1,000,000.`;
  const shareUrl = typeof window !== "undefined" ? window.location.origin : "";
  const shareLinks = [
    { id: "x", label: "X", href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}` },
    { id: "wa", label: "WhatsApp", href: `https://wa.me/?text=${encodeURIComponent(shareText + "\n" + shareUrl)}` },
    { id: "tg", label: "Telegram", href: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}` },
    { id: "fb", label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}` },
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* noop */
    }
  };

  const swallowingPhase = phase === "showing" || phase === "deforming" || phase === "swallowed" || phase === "done";
  const revealed = phase === "swallowed" || phase === "done";

  return (
    <div className="relative w-full h-screen overflow-hidden" data-testid="payment-success" style={{ background: "#000" }}>
      <BlackHole intensity={1.25} />

      {/* extra vignette while swallowing */}
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 2,
          pointerEvents: "none",
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0.85) 0%, transparent 20%, transparent 80%, rgba(0,0,0,0.85) 100%)",
        }}
      />

      <div className="absolute inset-0 z-10 flex flex-col items-center justify-center px-6 text-center">
        {phase === "verifying" && (
          <div
            className="font-serif text-white/70 drift-in"
            data-testid="verifying"
            style={{ fontSize: "clamp(20px, 2.6vw, 28px)", fontVariationSettings: '"opsz" 96, "SOFT" 20' }}
          >
            Approaching the horizon…
          </div>
        )}

        {swallowingPhase && (
          <>
            <div style={{ minHeight: "60px" }}>
              <SwallowLetters phrase={SYMBOLIC_PHRASE} phase={phase} />
            </div>

            {revealed && (
              <>
                <div
                  className="font-mono-void text-white drift-in"
                  style={{
                    fontSize: "clamp(36px, 6.5vw, 72px)",
                    letterSpacing: "0.34em",
                    marginTop: "48px",
                    textShadow: "0 0 40px rgba(255,220,180,0.35)",
                    animation: "pulse-glow 1200ms ease-out",
                  }}
                  data-testid="swallowed-word"
                >
                  SWALLOWED
                </div>
                <div
                  className="mt-8 font-serif text-white/60 drift-in drift-delay-1"
                  style={{ fontSize: "clamp(18px, 2vw, 24px)", fontVariationSettings: '"opsz" 96, "SOFT" 20' }}
                  data-testid="swallowed-caption"
                >
                  It is in the dark now.
                </div>
                {state.swallowed_number && (
                  <div
                    className="mt-16 font-ui text-white/45 drift-in drift-delay-2"
                    style={{ fontSize: "11px", letterSpacing: "0.4em", textTransform: "uppercase" }}
                    data-testid="swallowed-position"
                  >
                    You are one of{" "}
                    <span className="font-mono-void text-white/90 ml-2">
                      {nf.format(state.swallowed_number)}
                    </span>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {phase === "done" && (
          <div className="mt-14 flex flex-col items-center gap-6 drift-in drift-delay-3" data-testid="done-panel">
            <button
              onClick={() => setShareOpen((v) => !v)}
              className="void-cta"
              data-testid="share-btn"
            >
              Whisper it forward
            </button>
            {shareOpen && (
              <div
                className="void-glass px-6 py-4 flex flex-wrap items-center gap-6 rounded-full"
                data-testid="share-panel"
              >
                {shareLinks.map((s) => (
                  <a
                    key={s.id}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="void-chip"
                    data-testid={`share-${s.id}`}
                  >
                    {s.label}
                  </a>
                ))}
                <button onClick={copyLink} className="void-chip" data-testid="share-copy">
                  {copied ? "COPIED" : "COPY LINK"}
                </button>
              </div>
            )}
            <Link
              to="/"
              className="font-ui text-white/40 hover:text-white/85 transition-colors duration-500 mt-2"
              style={{ fontSize: "10px", letterSpacing: "0.36em", textTransform: "uppercase" }}
              data-testid="back-home"
            >
              Return to the void
            </Link>
          </div>
        )}

        {phase === "failed" && (
          <div className="text-center" data-testid="failed">
            <div
              className="font-serif text-white/80"
              style={{ fontSize: "clamp(20px, 2.6vw, 28px)", fontVariationSettings: '"opsz" 96, "SOFT" 10' }}
            >
              The darkness refused this offering.
            </div>
            <Link to="/" className="void-cta mt-10 inline-flex" data-testid="failed-return">
              Try again
            </Link>
          </div>
        )}

        {phase === "timeout" && (
          <div className="text-center" data-testid="timeout">
            <div
              className="font-serif text-white/70"
              style={{ fontSize: "clamp(18px, 2vw, 24px)", fontVariationSettings: '"opsz" 96, "SOFT" 20' }}
            >
              Still listening… refresh in a moment.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
